import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGet } from '@/api/hooks/useGet';
import { DeclineQuotation, DischargeQuotation, GetMedicalRequestDetails } from '@/api/endpoints';
import HeaderInfo from './components/HeaderInfo';
import RequestOverview from './components/RequestOverview';
import MedicalDiagnosis from './components/MedicalDiagnosis';
import BiometricInfo from './components/BiometricInfo';
import MedicalFamilyHistory from './components/MedicalFamilyHistory';
import PatientDocuments from './components/PatientDocuments';
import PatientRequestDetailsSkeleton from './components/PatientRequestDetailsSkeleton';
import './style.css';
import { PATIENT_REQUEST_STATUS, STATUS_MAP } from '@/data/status';
import { CRRender } from '@/components/crRender';
import { useRef, useState } from 'react';
import ChatWrapper from './components/chatWrapper';
import { successToast } from '@/utils/toasters';
import UploadPatientMedicalFiles from '@/components/popups/UploadPatientMedicalFiles';
import { Icon } from '@iconify/react/dist/iconify.js';
import { canRenderAddQuotation } from '../report';
const PatientRequestDetails = () => {
    const { id, statusId } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isUploaded, setIsUploaded] = useState(false);
    const refUploadPatientMedicalFilesModal = useRef(null);
    const[ dischargingLoading , setDischargingLoading] = useState(false);
    const[ decliningLoading , setDecliningLoading] = useState(false);
    const { result: requestDetails, isPending } = useGet({
        endPoint: GetMedicalRequestDetails,
        depArr: [isUploaded],
        allowFetch: !!id,
        params: [id]
    });

    const {state : {requestDetails :reqData}} = useLocation();



    const handleBack = () => {
        navigate(-1);
    };

    const handleRequestMoreInfo = () => {
        setIsChatOpen(true);
    };

    const handleAddQuestion = () => {
        navigate('/panel/provider/medical-request/add-quotation', {
            state: {
                requestId: id,
                patientName: requestDetails?.patientData?.patientName,
                serviceContext: {
                    category: requestDetails?.requestData?.category,
                    subcategory: requestDetails?.requestData?.subcategory,
                    service: requestDetails?.requestData?.service,
                    age: requestDetails?.patientData?.age,
                    requestId: requestDetails?.requestData?.id
                }
            }
        });
    };


    const handleDeclineAdmission = () => {
        setDecliningLoading(true);
        DeclineQuotation(id).then((res) => {
            successToast("declined successfully");
            navigate('/panel/provider/requests/medical');
        }).catch((err) => {
            errorToast("Error declining admission");
        }).finally(() => {
            setDecliningLoading(false);
        });
    };
    const handleDischargeAdmission = () => {
        setDischargingLoading(true);
        DischargeQuotation(id).then((res) => {
            successToast("discharged successfully");
            navigate('/panel/provider/requests/medical');
        }).catch((err) => {
            errorToast("Error discharging admission");
        }).finally(() => {
            setDischargingLoading(false);
        });
    };
    if (isPending) {
        return <PatientRequestDetailsSkeleton />;
    }


    return (
        <div className="patient-request-details-container">
            {/* Header Section */}
          
            <div className="patient-request-details-header">
                <div className="patient-request-details-header-left">
                    <button
                        className="patient-request-details-back-btn"
                        onClick={handleBack}
                    >
                        <Icon icon={`solar:arrow-${i18n.language === 'ar' ? 'right' : 'left'}-outline`} width={24} height={24} />
                        <span>{t('back')}</span>
                    </button>
                    <div className="patient-request-details-title-section">
                        <h1 className="patient-request-details-title">{t('patient-request-details')}</h1>
                        <p className="patient-request-details-subtitle">{t('detailed-clinical-intake-form')}</p>
                    </div>
                </div>
                <div className="patient-request-details-header-right">
                    <CRRender
                        isRender={![PATIENT_REQUEST_STATUS?.Declined,
                        PATIENT_REQUEST_STATUS?.Discharged,
                        PATIENT_REQUEST_STATUS?.Completed , 
                        PATIENT_REQUEST_STATUS?.InProgress , 
                    ]
                            .includes(Number(statusId))}>

                        <button
                            className="patient-request-details-request-info-btn"
                            onClick={handleRequestMoreInfo}
                        >
                            {t('request-more-info')}
                        </button>
                    </CRRender>
                    <CRRender
                        isRender={[PATIENT_REQUEST_STATUS?.InProgress,
                        PATIENT_REQUEST_STATUS?.Completed,
                        PATIENT_REQUEST_STATUS?.Discharged]
                            .includes(Number(statusId))}>

                        <button
                            className="patient-request-details-request-info-btn"
                            onClick={() => refUploadPatientMedicalFilesModal.current?.click()}
                        >
                            {t('upload-documents')}
                        </button>
                    </CRRender>

                    <CRRender
                        isRender={canRenderAddQuotation(statusId, reqData?.requestStatusId)}>
                        <button
                            className="patient-request-details-add-question-btn"
                            onClick={handleAddQuestion}
                        >
                            {t('add-question')}
                        </button>
                    </CRRender>
                    <CRRender
                        isRender={[PATIENT_REQUEST_STATUS?.Pending,
                        PATIENT_REQUEST_STATUS?.AwaitingInitialPayment,
                        PATIENT_REQUEST_STATUS?.InformationResponded ,
                        PATIENT_REQUEST_STATUS?.InformationRequested ]
                            .includes(Number(statusId))}>
                        <button
                            className="patient-request-details-decline-admission-btn"
                            onClick={handleDeclineAdmission}
                            disabled={decliningLoading}>
                            {decliningLoading ? <Icon icon="eos-icons:loading" width={24} height={24} /> : t('Decline Admission')}
                        </button>
                    </CRRender>
                    <CRRender
                        isRender={[PATIENT_REQUEST_STATUS?.Completed]
                            .includes(Number(statusId))}>
                        <button
                            className="patient-request-details-discharge-admission-btn"
                            onClick={handleDischargeAdmission}
                            disabled={dischargingLoading}>
                            {dischargingLoading ? <Icon icon="eos-icons:loading" width={24} height={24} /> : t('Discharge Admission')}
                        </button>
                    </CRRender>
                </div>
            </div>

            <div className="patient-request-details-content">

                <CRRender isRender={!!isChatOpen}>
                    <ChatWrapper providerOrderId={id} isChatOpen={isChatOpen} setIsChatOpen={setIsChatOpen} />
                </CRRender>

                <HeaderInfo data={{ ...requestDetails?.patientData, ...requestDetails?.requestData, requestId: id }} />

                <RequestOverview data={requestDetails?.requestData} />

                <MedicalDiagnosis data={{ ...requestDetails?.patientData, ...requestDetails?.requestData }} />

                <BiometricInfo data={{ ...requestDetails?.patientData, ...requestDetails?.requestData }} />

                <MedicalFamilyHistory data={{ ...requestDetails?.patientData, ...requestDetails?.requestData }} />

                <PatientDocuments data={{ ...requestDetails?.patientData, ...requestDetails?.requestData }} />
                <UploadPatientMedicalFiles setIsUploaded={setIsUploaded} modalID={refUploadPatientMedicalFilesModal} patientId={Number(requestDetails?.patientData?.id)} />
            </div>

        </div>
    );
};

export default PatientRequestDetails;
